import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { RecheckSimilarModal, type RecheckTarget } from '@client/components/_shared/auto-detect/RecheckSimilarModal';
import { SkipRuleAction } from '@client/components/_shared/auto-detect/SkipRuleAction';
import { preloadMarkdownRenderer } from '@client/components/_ui/display/Markdown';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { hasBlockingQueryError } from '@client/trpc/error-classification';
import { ENABLE_AUTO_DETECT_RECORD_STEP_FLAG, ENABLE_CLASSIFICATION_LEARNING_FLAG } from '@shared/constants';
import { PERMISSIONS } from '@shared/permissions/permissions';
import type { MessageClassificationClaimLinkSource } from '@shared/prisma/enums';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, Form, Skeleton } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInboxFilterLabel, useInboxSubject, useInboxVisibleIds } from '../inbox-navigation-store';
import type { AiOriginalClassification } from './ai-original-classification';
import { ClassificationUnavailable } from './ClassificationUnavailable';
import type { ClaimDecisionState } from './LinkedClaimsCard';
import {
    countBreachedObligations,
    countObligationOverrides,
    countVisibleObligations,
    hasUnresolvedAiBreach,
} from './ObligationVerdictList';
import {
    buildObligationReviewPayload,
    hydrateObligationReviews,
    type ObligationReviewEntry,
    type ObligationReviewState,
} from './obligation-review-state';
import {
    ReviewDecisionRail,
    ReviewDecisionRailFooter,
    ReviewDecisionRailHeader,
    TeachOutcomeCaption,
} from './ReviewDecisionRail';
import {
    type FeedbackEntry,
    type FeedbackField,
    type FeedbackState,
    hasUnsavedFeedbackChanges,
} from './ReviewerDisagreementSection';
import { ReviewWorkspace } from './ReviewWorkspace';
import { ReviewWorkspaceTitle } from './ReviewWorkspaceTitle';
import {
    canSkipClassification,
    canSubmitClassificationReview,
    resolveReviewHydration,
    reviewClassificationRefetchInterval,
} from './review-hydration';
import {
    consumeTeachOnSubmit,
    type DeferredReviewAction,
    fanOutCombinedFeedback,
    hasUnsavedReviewState,
    isBreachOnlyDecision,
    resolveDeferredReviewAction,
    resolveReviewSubmitDestination,
    resolveTeachPreviewOutcome,
    teachingNote,
} from './review-learning-state';
import {
    type RecordStepSource,
    type ReviewRecordKind,
    readRecordStepPreference,
    recordStepClaimId,
    recordStepForm,
    recordStepKinds,
    writeRecordStepPreference,
} from './review-record-step';
import { SkipDialog } from './SkipDialog';
import { useRecordStepAccess } from './use-record-step-access';
import { useReviewAiOriginalClassification } from './use-review-ai-original-classification';
import { useReviewDrawerHotkeys } from './use-review-drawer-hotkeys';
import { useReviewerActed } from './use-reviewer-acted';
import { useTeachSettleDeadline } from './use-teach-settle-deadline';

export const Route = createFileRoute('/console/$orgId/tools/auto-detect/inbox/$id/review-email-classification')({
    params: {
        parse({ id }) {
            return {
                id: BigInt(id),
            };
        },
    },
    // The review workspace renders the AI summary as markdown on load, so wait for the renderer.
    loader: preloadMarkdownRenderer,
    component: ReviewEmailClassification,
    staticData: {
        presentation: 'overlay',
        modules: ['auto-detect'],
        permissions: [PERMISSIONS.MESSAGE_CLASSIFICATION_ACCESS],
    },
});

type ReviewInput = RouterInputs['messageClassification']['reviewMessageClassification'];
type ReviewResult = RouterOutputs['messageClassification']['reviewMessageClassification'];
type SkipInput = RouterInputs['messageClassification']['skipMessageClassification'];
type FormValues = ReviewInput['set'];
type Data = RouterOutputs['messageClassification']['getMessageClassification'];

const REVIEW_FORM_FIELD_NAMES = [
    'isIncident',
    'isComplaint',
    'confidence',
    'isBreachOfRegulation',
    'breachedRegulation',
    'justification',
    'tags',
] as const satisfies ReadonlyArray<keyof FormValues>;

const FEEDBACK_FIELDS = [
    'isIncident',
    'isComplaint',
    'confidence',
    'isBreachOfRegulation',
] as const satisfies ReadonlyArray<FeedbackField>;

function ReviewEmailClassification() {
    const { id } = Route.useParams();
    const orgId = useOrgId();
    const user = useUser();
    const { trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const enableDebugInfo = useFeatureFlag('enable-debug-info');
    const enableClassificationLearning = useFeatureFlag(ENABLE_CLASSIFICATION_LEARNING_FLAG, false);
    // Off by default: with it off the rail offers no record step and Confirm moves to the next message.
    const enableRecordStep = useFeatureFlag(ENABLE_AUTO_DETECT_RECORD_STEP_FLAG, false);
    const [open, setOpen] = useState(true);
    // The overlay's `requestClose`: asks "Discard unsaved changes?" when `isReviewDirty` says so,
    // then closes. Captured from the render prop so the Escape hotkey and Cancel share it with
    // the header cross.
    const requestCloseRef = useRef<(() => void) | null>(null);
    // Set for a queue move performed right after a commit (Submit and next, Mark as not
    // relevant): the draft it leaves behind is settled, so the overlay's navigation guard must
    // not ask about it. The state it would read has not re-rendered clean at that point.
    // Cleared once the route shows the next record.
    const committedMoveRef = useRef(false);
    useEffect(() => {
        committedMoveRef.current = false;
    }, [id]);
    const [skipOpen, setSkipOpen] = useState(false);
    // Messages submitted without leaving the drawer, for the queue strip's session progress.
    const [reviewedThisSession, setReviewedThisSession] = useState(0);
    // CUR-886 — "No issue" quick verdict: swaps the per-axis reason capture for
    // one combined reason (plan §7.1). Reset whenever a different record hydrates.
    const [quickNoIssue, setQuickNoIssue] = useState(false);
    // "Then create … record" in the rail footer. Not reset per message: it is the reviewer's way of
    // working, remembered on this browser (finding 5 of the October 2026 UX review).
    const [recordStepEnabled, setRecordStepEnabled] = useState(readRecordStepPreference);
    const recordStepAccess = useRecordStepAccess();
    const handleRecordStepChange = useCallback((enabled: boolean) => {
        setRecordStepEnabled(enabled);
        writeRecordStepPreference(enabled);
    }, []);
    // CUR-886 — pre-submit teach opt-in (decided on the message it applies to)
    // and the retroactive re-check modal. The queue advance waits for the teach
    // flow so the offer always opens over the message it was learned from.
    const [teachOnSubmit, setTeachOnSubmit] = useState(false);
    const [recheckTarget, setRecheckTarget] = useState<RecheckTarget | null>(null);
    // True while the post-submit teach + similarity preview is in flight. Keeps the
    // commit action busy and the pager frozen so the reviewer isn't left on an
    // apparently-idle screen that then jumps.
    const { isSettling: isTeachSettling, settle: settleTeach } = useTeachSettleDeadline();
    // Live route identity for the teach flow's anchor check. The teach closure
    // captures the record it was taught from, so it needs a value that tracks
    // where the reviewer actually is now, not where they were at submit time.
    const currentIdRef = useRef(id);
    currentIdRef.current = id;
    // Every accepted submission gets an identity synchronously, before its
    // mutation starts. A later submission supersedes the previous teach even
    // when it does not teach itself or while its save is still in flight.
    const reviewSubmissionGenerationRef = useRef(0);
    // An edit after an accepted submission only cancels that submission's held
    // queue move. It must not hide the learned-decision offer while the reviewer
    // remains on the message the offer belongs to.
    const reviewEditedAfterSubmitRef = useRef(false);
    // Whether the reviewer has acted on this message, for the rail header's AI chip. Every edit
    // path goes through `markDeferredReviewEdited`, which marks it; a saved review opens marked.
    const {
        touched: reviewerActed,
        markActed: markReviewerActed,
        onHydrate: hydrateReviewerActed,
        onCommitted: markReviewCommitted,
    } = useReviewerActed(id);
    useEffect(
        () => () => {
            reviewSubmissionGenerationRef.current += 1;
        },
        [],
    );

    const { data, isPending, error, refetch } = trpc.messageClassification.getMessageClassification.useQuery(
        { id },
        { refetchInterval: reviewClassificationRefetchInterval, meta: { errorSurface: 'inline' } },
    );
    const hasBlockingError = hasBlockingQueryError(error, data);
    const isClassificationPending = data?.status === 'PENDING';
    const isClassificationFailed = data?.status === 'ERROR';
    const canSubmitReview = data && !hasBlockingError ? canSubmitClassificationReview(data.status) : false;
    const reviewEditingEnabled = canSubmitReview && !isTeachSettling;
    const markDeferredReviewEdited = useCallback(() => {
        if (!reviewEditingEnabled) return;
        reviewEditedAfterSubmitRef.current = true;
        markReviewerActed();
    }, [reviewEditingEnabled, markReviewerActed]);
    const invalidateDeferredTeach = useCallback(() => {
        reviewSubmissionGenerationRef.current += 1;
    }, []);
    const canSkip = data && !hasBlockingError ? canSkipClassification(data.status) : false;
    const { mutate: updateMessageClassification, isPending: isSubmitting } =
        trpc.messageClassification.reviewMessageClassification.useMutation();
    const { mutate: skipMessageMutation, isPending: isSkipping } =
        trpc.messageClassification.skipMessageClassification.useMutation();
    const { mutateAsync: teachExample } = trpc.messageClassification.teachClassificationExample.useMutation();
    const { data: availableTags } = trpc.messageClassification.loadTags.useQuery();

    // Drawer navigation — pager pulls visible row IDs + the active
    // filter label from the inbox so we can render "X of Y in <filter>".
    const visibleIds = useInboxVisibleIds();
    const filterLabel = useInboxFilterLabel();
    const { currentIndex, prevId, nextId } = useMemo(() => {
        const idx = visibleIds.findIndex((vid) => vid === id);
        return {
            currentIndex: idx,
            prevId: idx > 0 ? visibleIds[idx - 1] : null,
            nextId: idx >= 0 && idx < visibleIds.length - 1 ? visibleIds[idx + 1] : null,
        };
    }, [visibleIds, id]);

    // Subject of the message Next would land on — the queue strip shows it so "Next" is a
    // decision (another breach? a newsletter?) rather than a leap.
    const nextSubject = useInboxSubject(nextId);

    function navigateToId(targetId: bigint, options?: { afterCommit: boolean }) {
        if (options?.afterCommit) committedMoveRef.current = true;
        navigate({
            to: '/console/$orgId/tools/auto-detect/inbox/$id/review-email-classification',
            params: { orgId, id: targetId },
        });
    }

    // Live queue for the deferred moves below. They are captured at submit time inside closures
    // and performed after the teach flow, by which point the inbox has refetched at least once.
    const visibleIdsRef = useRef(visibleIds);
    visibleIdsRef.current = visibleIds;

    // Perform a queue move captured earlier against the inbox as it is NOW. The captured id can
    // have left the queue in the meantime (deleted, reviewed by someone else, filtered out by the
    // post-submit refetch). Best effort: the list is only as fresh as its last refetch, so a row
    // that vanished since then still gets opened — the unavailable render state is the backstop.
    function performDeferredReviewAction(action: DeferredReviewAction) {
        const resolved = resolveDeferredReviewAction({
            action,
            currentId: currentIdRef.current,
            visibleIds: visibleIdsRef.current,
        });
        if (resolved.kind === 'record') {
            openRecordStep(resolved);
        } else if (resolved.kind === 'next') {
            navigateToId(resolved.nextId);
        } else {
            setOpen(false);
        }
    }

    // Leave the committed review for its record form(s), which return to the queue in turn.
    function openRecordStep(action: Extract<DeferredReviewAction, { kind: 'record' }>) {
        const first = recordStepForm(action.kinds, action.source, action.nextId);
        committedMoveRef.current = true;
        if (first.record === 'complaint') {
            navigate({ to: '/console/$orgId/compliance/complaints/add', params: { orgId }, search: first.search });
            return;
        }
        navigate({ to: '/console/$orgId/compliance/incidents/add', params: { orgId }, search: first.search });
    }

    const [form] = Form.useForm<FormValues>();
    // Keyed by id + AI-output identity, not id alone: a rerun/reprocess that
    // completes while the drawer is open replaces the record's verdict under
    // us, and the refetch must rehydrate the form. Keying on the id only left
    // the form showing the PREVIOUS verdict while the AI panes showed the new
    // one — manufacturing "changes from the AI" the reviewer never made.
    // `processedAt`/`llmCorrelationId` change on every (re)classification but
    // not on review submits, so "Save and stay" keeps its feedback baseline.
    const hydratedReviewKeyRef = useRef<string | null>(null);
    const pendingNotificationIdRef = useRef<bigint | null>(null);
    // Set by "Save and stay" so the shared submit handler skips the advance-to-next step. A ref
    // rather than state: it is read once inside the mutation callback and must not re-render.
    const stayAfterSubmitRef = useRef(false);
    // Set when a teach flow ends in a re-check offer: the navigation it interrupted is handed to
    // the modal's onClose. Advancing (or closing the drawer) sooner would move the offer onto a
    // message it says nothing about — the confusion this defers around.
    const afterRecheckRef = useRef<DeferredReviewAction | null>(null);
    const isBreachOfRegulation = Form.useWatch('isBreachOfRegulation', form);
    const watchedIsIncident = Form.useWatch('isIncident', form);
    const watchedIsComplaint = Form.useWatch('isComplaint', form);
    const watchedConfidence = Form.useWatch('confidence', form);
    // Live view of the drafted verdict for the breach-only teach exclusion —
    // undefined (pre-hydration) reads as false on every axis.
    const draftedBreachOnly = isBreachOnlyDecision({
        isIncident: watchedIsIncident === true,
        isComplaint: watchedIsComplaint === true,
        isBreachOfRegulation: isBreachOfRegulation === true,
    });
    const aiOriginalClassification = useReviewAiOriginalClassification(data);

    // Per-obligation reviews, claim link decisions, and AI-disagreement
    // feedback live outside the Ant Form (their shape doesn't fit
    // Form.Item ergonomics). State is hydrated from server-returned
    // data and reset whenever we switch messages.
    const [obligationReviews, setObligationReviews] = useState<ObligationReviewState>({});
    const [claimDecisions, setClaimDecisions] = useState<ClaimDecisionState>({});
    const [feedback, setFeedback] = useState<FeedbackState>({});
    const [savedFeedback, setSavedFeedback] = useState<FeedbackState>({});

    // Obligation reviews hydrate from data so referential equality alone
    // isn't dirty. Flip this on the first user-driven change and reset
    // on hydrate so the unsaved-changes guard catches per-obligation
    // edits that don't touch the main form.
    const [obligationReviewsDirty, setObligationReviewsDirty] = useState(false);

    // Track whether the reviewer pulled in the AI-rendered Justification
    // / Breached regulation text via the "Use AI reasoning" CTA. Any
    // subsequent edit flips the flag back to false so the audit log
    // can distinguish "reviewer signed off on AI prose" from
    // "reviewer wrote their own prose".
    const [justificationDerivedFromAi, setJustificationDerivedFromAi] = useState(false);
    const [breachDetailsDerivedFromAi, setBreachDetailsDerivedFromAi] = useState(false);

    useEffect(() => {
        if (!data) return;
        const hydration = resolveReviewHydration(hydratedReviewKeyRef.current, data);
        if (hydration.kind === 'pending' || hydration.kind === 'failed') {
            hydratedReviewKeyRef.current = hydration.key;
            if (!hydration.sameRecord) {
                // A queued/failed row opened through queue navigation has no
                // reviewable output. Do not leak the previous record's form
                // state into its read-only workspace.
                form.resetFields();
                setObligationReviews({});
                setObligationReviewsDirty(false);
                setClaimDecisions({});
                setFeedback({});
                setSavedFeedback({});
                setJustificationDerivedFromAi(false);
                setBreachDetailsDerivedFromAi(false);
                setQuickNoIssue(false);
                setTeachOnSubmit(false);
                hydrateReviewerActed(data.id, false);
            }
            if (hydration.kind === 'pending' && hydration.sameRecord && pendingNotificationIdRef.current !== data.id) {
                message.info('This message is being re-classified — review submission is paused until it finishes.');
            }
            pendingNotificationIdRef.current = hydration.kind === 'pending' ? data.id : null;
            return;
        }
        pendingNotificationIdRef.current = null;
        if (hydration.kind === 'unchanged') return;

        // Same record, different AI output — the record was re-classified while
        // the drawer was open. Tell the reviewer why their in-progress state is
        // being replaced instead of silently swapping it.
        if (hydration.reclassified) {
            message.info('This message was re-classified — the review form has been refreshed with the new AI output.');
        }

        hydratedReviewKeyRef.current = hydration.key;

        // Hydrate the form. Note: justification and breachedRegulation
        // are intentionally NOT pre-populated from the AI's text — the
        // reviewer must explicitly opt in via "Use AI reasoning" so the
        // audit log isn't padded with rubber-stamped AI prose.
        const hasPersistedReviewerValues = hydration.hasPersistedReviewerValues;
        const nextValues = {
            isIncident: data.isIncident ?? false,
            isComplaint: data.isComplaint ?? false,
            confidence: data.confidence || 'LOW',
            isBreachOfRegulation: data.isBreachOfRegulation || false,
            // Show previously-reviewed prose verbatim, but on a fresh
            // record start empty (AI text is offered as a sidecar
            // alongside, not silently injected).
            breachedRegulation: hasPersistedReviewerValues ? data.breachedRegulation || '' : '',
            justification: hasPersistedReviewerValues ? data.justification || '' : '',
            tags: data.tags || [],
            justificationDerivedFromAi: false,
            breachDetailsDerivedFromAi: false,
        } satisfies Partial<FormValues>;

        form.setFieldsValue(nextValues);
        form.setFields(REVIEW_FORM_FIELD_NAMES.map((name) => ({ name, touched: false })));

        setObligationReviews(hydrateObligationReviews(data));
        setObligationReviewsDirty(false);
        setClaimDecisions({});
        setFeedback({});
        setSavedFeedback({});
        setJustificationDerivedFromAi(false);
        setBreachDetailsDerivedFromAi(false);
        setQuickNoIssue(false);
        setTeachOnSubmit(false);
        hydrateReviewerActed(data.id, hasPersistedReviewerValues);
    }, [data, form, hydrateReviewerActed]);

    useEffect(() => {
        if (!canSkip) setSkipOpen(false);
    }, [canSkip]);

    // Build the disagreement diff: any field that differs from the AI's
    // pre-review value needs a reasonCode before we let the form submit.
    // The two classification axes are diffed INDEPENDENTLY — flipping both
    // produces two feedback rows, matching the FEEDBACK_FIELDS check
    // constraint on MessageClassificationFeedback.
    const differingFields = useMemo<FeedbackField[]>(() => {
        if (!aiOriginalClassification) return [];
        const diffs: FeedbackField[] = [];
        if (
            typeof watchedIsIncident === 'boolean' &&
            typeof aiOriginalClassification.isIncident === 'boolean' &&
            watchedIsIncident !== aiOriginalClassification.isIncident
        ) {
            diffs.push('isIncident');
        }
        if (
            typeof watchedIsComplaint === 'boolean' &&
            typeof aiOriginalClassification.isComplaint === 'boolean' &&
            watchedIsComplaint !== aiOriginalClassification.isComplaint
        ) {
            diffs.push('isComplaint');
        }
        if (
            watchedConfidence &&
            aiOriginalClassification.confidence &&
            watchedConfidence !== aiOriginalClassification.confidence
        ) {
            diffs.push('confidence');
        }
        if (
            typeof isBreachOfRegulation === 'boolean' &&
            typeof aiOriginalClassification.isBreachOfRegulation === 'boolean' &&
            isBreachOfRegulation !== aiOriginalClassification.isBreachOfRegulation
        ) {
            diffs.push('isBreachOfRegulation');
        }
        return diffs;
    }, [aiOriginalClassification, watchedIsIncident, watchedIsComplaint, watchedConfidence, isBreachOfRegulation]);

    // CUR-886 §7.1 — the quick action legitimately raises certainty to High, so
    // a certainty-only divergence must not demand a reason on the one-click
    // path. All other axes keep the reason requirement (served by the combined
    // panel when the quick action is active).
    const reasonRequiredFields = useMemo(
        () => (quickNoIssue ? differingFields.filter((field) => field !== 'confidence') : differingFields),
        [differingFields, quickNoIssue],
    );

    // Fan one combined reason out to every differing feedback field — the
    // per-field rows keep their own aiValue/reviewerValue, only the reason
    // (and optional note) is shared.
    const handleCombinedFeedbackChange = useCallback(
        (patch: Partial<FeedbackEntry>) => {
            if (!aiOriginalClassification || !reviewEditingEnabled) return;
            markDeferredReviewEdited();
            setFeedback((prev) => {
                const values = Object.fromEntries(
                    FEEDBACK_FIELDS.map((field) => [
                        field,
                        {
                            aiValue: aiValueForField(aiOriginalClassification, field),
                            reviewerValue: reviewerValueForField(form, isBreachOfRegulation, field),
                        },
                    ]),
                ) as Record<FeedbackField, Pick<FeedbackEntry, 'aiValue' | 'reviewerValue'>>;
                return fanOutCombinedFeedback({ fields: differingFields, previous: prev, values, patch });
            });
        },
        [
            aiOriginalClassification,
            reviewEditingEnabled,
            markDeferredReviewEdited,
            form,
            isBreachOfRegulation,
            differingFields,
        ],
    );

    // The diff-set can grow AFTER the combined reason was chosen (the quick verdict or a manual
    // control edit flips another axis) — the new row would block submit while the panel still
    // renders the chosen chip. Re-fan the chosen reason out whenever the set changes.
    useEffect(() => {
        if (!quickNoIssue) return;
        // The panel displays the entry keyed off the first differing field — that IS the combined reason.
        const combined = differingFields[0] ? feedback[differingFields[0]] : undefined;
        if (!combined?.reasonCode) return;
        if (!reasonRequiredFields.some((field) => !feedback[field]?.reasonCode)) return;
        handleCombinedFeedbackChange({ reasonCode: combined.reasonCode, note: combined.note });
    }, [quickNoIssue, differingFields, feedback, reasonRequiredFields, handleCombinedFeedbackChange]);

    // Catch every shape of dirty state — form fields, obligation
    // toggle/note edits, claim CONFIRM/REJECT clicks, disagreement
    // chips/notes, and the "Use AI reasoning" buttons (which set
    // form values programmatically and don't trip Ant's touched
    // tracking on their own). `OverlayPage` reads it when the drawer is
    // dismissed (Escape, the cross, Cancel) and when in-app navigation or
    // browser unload would leave this record.
    //
    // Both `claimDecisions` and `feedback` keep stale entries after the
    // reviewer reverts a change (decision back to null, classification
    // back to the AI's value). Filter those out so the discard modal
    // only fires when something is actually unsaved.
    const isReviewDirty = useCallback(() => {
        if (committedMoveRef.current) return false;
        const hasActiveClaimDecision = Object.values(claimDecisions).some((entry) => entry.decision !== null);
        const hasUnsavedFeedback = hasUnsavedFeedbackChanges(differingFields, feedback, savedFeedback);
        return hasUnsavedReviewState({
            formTouched: form.isFieldsTouched(),
            obligationReviewsDirty,
            hasActiveClaimDecision,
            hasUnsavedFeedback,
            justificationDerivedFromAi,
            breachDetailsDerivedFromAi,
            teachOnSubmit,
        });
    }, [
        form,
        obligationReviewsDirty,
        claimDecisions,
        feedback,
        savedFeedback,
        differingFields,
        justificationDerivedFromAi,
        breachDetailsDerivedFromAi,
        teachOnSubmit,
    ]);

    const attemptClose = useCallback(() => {
        // Closing navigates back to the inbox and unmounts this route, which would
        // drop the in-flight teach: its re-check offer would never render and its
        // deferred queue advance would fire from a dead component, dragging the
        // reviewer back into a drawer they just left. The settle deadline below
        // guarantees this temporary hold cannot become a keyboard trap.
        if (isTeachSettling) return;
        requestCloseRef.current?.();
    }, [isTeachSettling]);

    const handleObligationReviewChange = useCallback(
        (obligationId: string, patch: Partial<ObligationReviewEntry>) => {
            if (!reviewEditingEnabled) return;
            markDeferredReviewEdited();
            setObligationReviewsDirty(true);
            setObligationReviews((prev) => ({
                ...prev,
                [obligationId]: {
                    relevant: prev[obligationId]?.relevant ?? true,
                    breach: prev[obligationId]?.breach ?? null,
                    note: prev[obligationId]?.note ?? '',
                    ...patch,
                },
            }));
        },
        [reviewEditingEnabled, markDeferredReviewEdited],
    );

    const handleClaimDecisionChange = useCallback(
        (claimId: string, source: MessageClassificationClaimLinkSource, decision: 'CONFIRM' | 'REJECT' | null) => {
            if (!reviewEditingEnabled) return;
            markDeferredReviewEdited();
            setClaimDecisions((prev) => {
                return {
                    ...prev,
                    [claimId]: { source, decision },
                };
            });
        },
        [reviewEditingEnabled, markDeferredReviewEdited],
    );

    const handleFeedbackChange = useCallback(
        (field: FeedbackField, patch: Partial<FeedbackEntry>) => {
            if (!aiOriginalClassification || !reviewEditingEnabled) return;
            markDeferredReviewEdited();
            setFeedback((prev) => {
                const aiValue = aiValueForField(aiOriginalClassification, field);
                const reviewerValue = reviewerValueForField(form, isBreachOfRegulation, field);
                return {
                    ...prev,
                    [field]: {
                        aiValue,
                        reviewerValue,
                        reasonCode: prev[field]?.reasonCode ?? null,
                        note: prev[field]?.note ?? '',
                        ...patch,
                    },
                };
            });
        },
        [aiOriginalClassification, reviewEditingEnabled, markDeferredReviewEdited, form, isBreachOfRegulation],
    );

    // Keep the cached AI/reviewer values fresh as the form changes —
    // without this, the disagreement card shows stale values when the
    // reviewer toggles back and forth.
    useEffect(() => {
        if (!aiOriginalClassification) return;
        setFeedback((prev) => {
            const next: FeedbackState = { ...prev };
            for (const field of FEEDBACK_FIELDS) {
                const aiValue = aiValueForField(aiOriginalClassification, field);
                const reviewerValue = reviewerValueForField(form, isBreachOfRegulation, field);
                const existing = next[field];
                if (existing) {
                    next[field] = { ...existing, aiValue, reviewerValue };
                }
            }
            return next;
        });
    }, [
        aiOriginalClassification,
        watchedIsIncident,
        watchedIsComplaint,
        watchedConfidence,
        isBreachOfRegulation,
        form,
    ]);

    // CUR-886 — teach flow shared by every submit branch: persist the learned example, then
    // offer the retroactive re-check when similar unreviewed messages exist. Resolves true when
    // the re-check modal opened. Await-based rather than mutate-scoped callbacks: every branch
    // holds its navigation until this settles, because navigating away drops mutate-scoped
    // callbacks — a failed teach (or its re-check offer) would vanish silently.
    async function teachAndOfferRecheck(
        record: Data,
        feedbackPayload: ReviewInput['feedback'],
        reviewToken: ReviewResult['reviewToken'],
        deferredAction: DeferredReviewAction | null,
        submissionGeneration: number,
    ): Promise<void> {
        // Offers belong to the submission and message that created them. Edits
        // do not make that offer stale; they only make its held queue move stale.
        const isOfferStale = (): boolean =>
            reviewSubmissionGenerationRef.current !== submissionGeneration || currentIdRef.current !== record.id;
        // Hand the caller's queue move to whichever way the teach ends, exactly
        // once: an offer takes ownership of the move, no offer performs it now.
        let deferredSettled = false;
        const settleDeferred = (offerOpened: boolean): void => {
            if (deferredSettled || deferredAction === null) return;
            deferredSettled = true;
            if (isOfferStale() || reviewEditedAfterSubmitRef.current) return;
            if (offerOpened) {
                afterRecheckRef.current = deferredAction;
            } else {
                performDeferredReviewAction(deferredAction);
            }
        };

        if (!enableClassificationLearning) {
            settleDeferred(false);
            return;
        }
        const noun = record.messageType === 'SMS' ? 'messages' : 'emails';
        const sourceHeadline = record.subject || 'the reviewed message';
        message.loading(`Saving the learned decision and looking for similar ${noun}...`);

        const settled = await settleTeach(async () => {
            try {
                const result = await teachExample({
                    messageClassificationId: record.id,
                    reviewToken,
                    reasonCode: feedbackPayload?.[0]?.reasonCode,
                    note: teachingNote(feedbackPayload ?? []),
                });
                void trpcUtils.messageClassification.listLearnedExamples.invalidate();
                void trpcUtils.messageClassification.listLearnedExampleManagementMetadata.invalidate();
                if (isOfferStale()) return false;

                try {
                    // Fetch through the query cache so the RecheckSimilarModal's own
                    // useQuery reads this result instead of repeating the billed
                    // embedding + vector search on mount (its staleTime covers the gap).
                    const preview = await trpcUtils.messageClassification.previewSimilarToExample.fetch({
                        id: result.id,
                    });
                    // A deadline may have released the reviewer onto the next
                    // message while this unabortable query was still resolving.
                    // Never let its late offer attach to that new message.
                    if (isOfferStale()) return false;

                    const previewOutcome = resolveTeachPreviewOutcome(preview);
                    if (previewOutcome === 'unavailable') {
                        message.warning(
                            'Learned decision saved, but similar-message discovery is unavailable. Retry here or later from Learned decisions.',
                        );
                        setRecheckTarget({ exampleId: result.id, channel: record.messageType, sourceHeadline });
                        return true;
                    }
                    if (previewOutcome === 'matches' || previewOutcome === 'preparing') {
                        // Keep the reviewer here while indexing finishes. The modal
                        // polls sync status and refreshes the preview before selection.
                        setRecheckTarget({ exampleId: result.id, channel: record.messageType, sourceHeadline });
                        return true;
                    }
                    message.success(`Learned decision saved — no similar unreviewed ${noun} to re-check.`);
                } catch {
                    if (isOfferStale()) return false;
                    message.warning(
                        'Learned decision saved, but similar-message discovery failed. Retry from Learned decisions.',
                    );
                    setRecheckTarget({ exampleId: result.id, channel: record.messageType, sourceHeadline });
                    return true;
                }
            } catch (error) {
                if (!isOfferStale()) errorMessage.show(error);
            }
            return false;
        }, false);

        // Cancelled means the reviewer left or a newer teach took over; whoever
        // did that owns the queue now, so this run's move is moot.
        if (settled.cancelled) return;
        if (settled.timedOut) {
            // The hold is released, not the work. Wait for how it actually ends
            // before moving the reviewer: an offer takes the queue move, no
            // offer performs it. Dropping the move here is what stranded
            // Submit-and-next whenever a slow teach found nothing to re-check.
            message.warning(
                'Learning is taking longer than expected — you have been kept on this message. Any re-check offer will open here; you can also find the outcome later in Learned decisions.',
            );
            void settled.completion.then((offerOpened) => {
                settleDeferred(offerOpened);
            });
            return;
        }
        settleDeferred(settled.value);
    }

    // After a successful submit the committed values become the clean baseline. Used by "Save and
    // stay" and by the last-item branch, which keeps the drawer open while the teach flow settles.
    function markReviewSaved() {
        form.setFields(REVIEW_FORM_FIELD_NAMES.map((name) => ({ name, touched: false })));
        setObligationReviewsDirty(false);
        setClaimDecisions({});
        // The saved overrides still differ from the AI, so retain their reasons
        // for subsequent edits and make them the new clean baseline.
        setSavedFeedback(feedback);
        setJustificationDerivedFromAi(false);
        setBreachDetailsDerivedFromAi(false);
    }

    async function handleSubmit(values: FormValues) {
        // Consume the "stay put" intent up front. Every early return below (and antd's own
        // validation, which never reaches this function) would otherwise leave the ref set, so a
        // later Submit-and-next would silently decline to advance.
        const stayAfterSubmit = stayAfterSubmitRef.current;
        stayAfterSubmitRef.current = false;

        if (!data || !canSubmitReview || isTeachSettling) return;

        // Block submit if any differing field is missing a reasonCode.
        const missingReason = reasonRequiredFields.find((field) => !feedback[field]?.reasonCode);
        if (missingReason) {
            message.error('Pick a reason for each value you changed from the AI.');
            return;
        }

        // Allocate this before dispatching the mutation. A second accepted
        // submit invalidates any older timed-out teach immediately, including
        // when the new submission does not opt into learning or later fails.
        const submissionGeneration = ++reviewSubmissionGenerationRef.current;
        reviewEditedAfterSubmitRef.current = false;

        const obligationReviewPayload = buildObligationReviewPayload(data.matchedObligations, obligationReviews);

        // Decided from the submitted values, not the watched ones, and only for records the message
        // does not have yet.
        const recordKinds: ReviewRecordKind[] =
            enableRecordStep && recordStepEnabled
                ? recordStepKinds({
                      isIncident: values.isIncident,
                      isComplaint: values.isComplaint,
                      hasIncident: (data.incidentIds?.length ?? 0) > 0,
                      hasComplaint: (data.complaintIds?.length ?? 0) > 0,
                      canCreate: recordStepAccess,
                  })
                : [];
        const recordSource: RecordStepSource = {
            messageClassificationId: data.id,
            // The matched-claim pre-link is complaint-only, as on the findings pane's cards.
            claimId: recordStepClaimId(data.matchedClaims, claimDecisions, data.linkedClaimDecisions),
            incidentId: data.incidentIds?.[0],
        };

        const linkedClaimPayload = buildLinkedClaimDecisionPayload(data, claimDecisions);

        const feedbackPayload = differingFields
            .map((field) => {
                const entry = feedback[field];
                if (!entry?.reasonCode) return null;
                return {
                    field,
                    aiValue: entry.aiValue,
                    reviewerValue: entry.reviewerValue,
                    reasonCode: entry.reasonCode,
                    note: entry.note || undefined,
                };
            })
            .filter((row): row is NonNullable<typeof row> => row !== null);

        message.loading('Reviewing classification...');
        updateMessageClassification(
            {
                id: data.id,
                expectedOutput: {
                    llmCorrelationId: data.llmCorrelationId,
                    processedAt: data.processedAt,
                },
                set: {
                    isIncident: values.isIncident,
                    isComplaint: values.isComplaint,
                    confidence: values.confidence,
                    isBreachOfRegulation: values.isBreachOfRegulation,
                    breachedRegulation: values.breachedRegulation,
                    justification: values.justification,
                    tags: values.tags,
                    justificationDerivedFromAi,
                    breachDetailsDerivedFromAi,
                },
                obligationReviews: obligationReviewPayload,
                linkedClaimDecisions: linkedClaimPayload,
                feedback: feedbackPayload,
            },
            {
                onSuccess(reviewResult) {
                    message.success('Classification reviewed successfully.');
                    trpcUtils.messageClassification.invalidate();
                    setReviewedThisSession((count) => count + 1);
                    // A committed review is the reviewer's own decision, even one that changed nothing:
                    // "Save and stay" must not leave the chip reading as the AI's suggestion.
                    markReviewCommitted(data.id);
                    // CUR-886 — the reviewer opted in on the rail's "Learn from
                    // this decision" BEFORE submitting, so teaching runs now,
                    // attributed to the message it belongs to. The re-check offer
                    // only opens when matches exist (it names its source, since
                    // the queue may already have advanced); zero matches is a
                    // toast, never a modal with a disabled "Re-check 0" button.
                    const teaching = consumeTeachOnSubmit({
                        teachOnSubmit: enableClassificationLearning && teachOnSubmit,
                        messageType: data.messageType,
                        // The token carries the adjudicated axes the server just
                        // committed — the authoritative values for the
                        // breach-only exclusion, not the pre-review record.
                        decision: reviewResult.reviewToken,
                    });
                    const shouldTeach = teaching.shouldTeach;
                    // Learning is a one-shot opt-in. Consume it before any async teach branch so
                    // Save and stay cannot teach the same row again on a later edit/save.
                    if (teachOnSubmit !== teaching.nextTeachOnSubmit) {
                        setTeachOnSubmit(teaching.nextTeachOnSubmit);
                    }
                    // "Save and stay" keeps the reviewer on the message they just committed —
                    // the escape hatch for when they want to re-read it or open the record they
                    // just created, instead of being carried to the next item.
                    const destination = resolveReviewSubmitDestination({
                        stayAfterSubmit,
                        nextId: nextId ?? undefined,
                        shouldTeach,
                        recordKinds,
                    });
                    if (destination.kind === 'stay') {
                        if (shouldTeach)
                            void teachAndOfferRecheck(
                                data,
                                feedbackPayload,
                                reviewResult.reviewToken,
                                null,
                                submissionGeneration,
                            );
                        markReviewSaved();
                        return;
                    }
                    if (destination.kind === 'record') {
                        const recordAction = {
                            kind: 'record',
                            kinds: recordKinds,
                            source: recordSource,
                            nextId: nextId ?? undefined,
                        } as const satisfies DeferredReviewAction;
                        if (!shouldTeach) {
                            openRecordStep(recordAction);
                            return;
                        }
                        // As for "next": the teach flow holds the move until its re-check offer
                        // (if any) closes, so the offer is about the message it was made for.
                        markReviewSaved();
                        void teachAndOfferRecheck(
                            data,
                            feedbackPayload,
                            reviewResult.reviewToken,
                            recordAction,
                            submissionGeneration,
                        );
                        return;
                    }
                    if (destination.kind === 'next') {
                        if (!shouldTeach) {
                            navigateToId(destination.nextId, { afterCommit: true });
                            return;
                        }
                        // Teaching holds the queue advance. Advancing first put the re-check
                        // offer over the NEXT message, where it reads as a question about that
                        // message rather than the one just reviewed. The saved baseline stops an
                        // interim Escape raising a false discard warning while it settles.
                        markReviewSaved();
                        void teachAndOfferRecheck(
                            data,
                            feedbackPayload,
                            reviewResult.reviewToken,
                            {
                                kind: 'next',
                                nextId: destination.nextId,
                            },
                            submissionGeneration,
                        );
                        return;
                    }
                    // Last message in the queue: the drawer must not close until the teach flow
                    // settles — closing navigates away and unmounts the route, silently dropping
                    // the teach outcome. A re-check offer takes over the close via its onClose.
                    if (destination.kind === 'last-item-teach') {
                        markReviewSaved();
                        void teachAndOfferRecheck(
                            data,
                            feedbackPayload,
                            reviewResult.reviewToken,
                            { kind: 'close' },
                            submissionGeneration,
                        );
                        return;
                    }
                    setOpen(false);
                },
                onError(error) {
                    // PRECONDITION means the hydrated output was superseded;
                    // refreshing this exact detail query gives the drawer the
                    // replacement identity and hydration decision immediately.
                    void trpcUtils.messageClassification.getMessageClassification.invalidate({ id: data.id });
                    errorMessage.show(error);
                },
            },
        );
    }

    function handleSkipSubmit(values: Pick<SkipInput, 'skipCategory' | 'note'>) {
        if (!data || !canSkip || isTeachSettling) return;
        // Opening the dialog already invalidates a late offer; repeat this at
        // the mutation boundary so submit stays safe if another entry path is added.
        invalidateDeferredTeach();
        message.loading('Skipping message...');
        skipMessageMutation(
            {
                id: data.id,
                skipCategory: values.skipCategory,
                note: values.note,
            },
            {
                onSuccess() {
                    message.success('Message marked as not relevant.');
                    trpcUtils.messageClassification.invalidate();
                    setSkipOpen(false);
                    if (nextId) {
                        navigateToId(nextId, { afterCommit: true });
                    } else {
                        setOpen(false);
                    }
                },
                onError(error) {
                    errorMessage.show(error);
                },
            },
        );
    }

    // Keyboard shortcut handlers (gated in useReviewDrawerHotkeys) are wrapped in
    // useCallback so the useHotkey effect doesn't re-attach ~13 keydown listeners
    // on every render.
    const submitForm = useCallback(() => form.submit(), [form]);
    const goNext = useCallback(() => {
        if (nextId) navigateToId(nextId);
        // navigateToId is stable enough — depends only on orgId from useOrgId
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nextId]);
    const goPrev = useCallback(() => {
        if (prevId) navigateToId(prevId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prevId]);
    const agreeWithAi = useCallback(() => {
        if (!data || !reviewEditingEnabled) return;
        markDeferredReviewEdited();
        form.setFieldsValue({
            isIncident: aiOriginalClassification?.isIncident ?? data.isIncident ?? false,
            isComplaint: aiOriginalClassification?.isComplaint ?? data.isComplaint ?? false,
            confidence: aiOriginalClassification?.confidence ?? data.confidence ?? 'LOW',
            isBreachOfRegulation: aiOriginalClassification?.isBreachOfRegulation ?? data.isBreachOfRegulation ?? false,
        });
    }, [data, reviewEditingEnabled, markDeferredReviewEdited, form, aiOriginalClassification]);
    const openSkipDialog = useCallback(() => {
        // Guarded here rather than per call site — the hotkey and the rail both
        // land on this. Skipping mid-teach would navigate away
        // from the message whose learned decision is still being saved.
        if (!canSkip || isTeachSettling) return;
        invalidateDeferredTeach();
        setSkipOpen(true);
    }, [canSkip, isTeachSettling, invalidateDeferredTeach]);
    const breachedObligationCount = useMemo(
        () => (data ? countBreachedObligations(data.matchedObligations, obligationReviews) : 0),
        [data, obligationReviews],
    );
    const obligationOverrideCount = useMemo(
        () => (data ? countObligationOverrides(data.matchedObligations, obligationReviews) : 0),
        [data, obligationReviews],
    );
    // CUR-886 — "No issue" quick verdict: pre-fills the negative verdict at
    // high certainty. When the AI itself found nothing (a certainty-only
    // confirmation), submit immediately — the true one-click path. Otherwise
    // the combined reason panel opens and the reviewer submits normally.
    const applyNoIssueQuickVerdict = useCallback(() => {
        if (!data || !reviewEditingEnabled) return;
        markDeferredReviewEdited();
        form.setFields([
            { name: 'isIncident', value: false, touched: true },
            { name: 'isComplaint', value: false, touched: true },
            { name: 'isBreachOfRegulation', value: false, touched: true },
            { name: 'confidence', value: 'HIGH', touched: true },
        ]);
        setQuickNoIssue(true);
        const aiWasNegative =
            (aiOriginalClassification?.isIncident ?? data.isIncident ?? false) === false &&
            (aiOriginalClassification?.isComplaint ?? data.isComplaint ?? false) === false &&
            (aiOriginalClassification?.isBreachOfRegulation ?? data.isBreachOfRegulation ?? false) === false;
        // A breached obligation verdict (or unsaved rail edits) contradicts
        // the negative verdict being submitted — hold the one-click submit so
        // the rail's contradiction warning can render and the reviewer
        // resolves it before submitting normally.
        if (aiWasNegative && breachedObligationCount === 0 && !obligationReviewsDirty) {
            form.submit();
        }
    }, [
        data,
        reviewEditingEnabled,
        markDeferredReviewEdited,
        form,
        aiOriginalClassification,
        breachedObligationCount,
        obligationReviewsDirty,
    ]);
    const toggleBreach = useCallback(() => {
        if (!reviewEditingEnabled) return;
        markDeferredReviewEdited();
        form.setFieldsValue({ isBreachOfRegulation: !isBreachOfRegulation });
    }, [reviewEditingEnabled, markDeferredReviewEdited, form, isBreachOfRegulation]);

    useReviewDrawerHotkeys(
        { open, skipOpen, recheckOpen: recheckTarget !== null, isTeachSettling, reviewEditingEnabled, canSkip },
        {
            submit: submitForm,
            close: attemptClose,
            next: goNext,
            prev: goPrev,
            agreeWithAi,
            openSkipDialog,
            toggleBreach,
            noIssue: applyNoIssueQuickVerdict,
        },
    );

    const aiJustification = aiOriginalClassification?.justification ?? '';
    const aiBreachText = aiOriginalClassification?.breachedRegulation ?? '';

    const useAiJustification = useCallback(() => {
        if (!aiJustification || !reviewEditingEnabled) return;
        markDeferredReviewEdited();
        form.setFieldsValue({ justification: aiJustification });
        setJustificationDerivedFromAi(true);
    }, [aiJustification, reviewEditingEnabled, markDeferredReviewEdited, form]);

    const useAiBreachText = useCallback(() => {
        if (!aiBreachText || !reviewEditingEnabled) return;
        markDeferredReviewEdited();
        form.setFieldsValue({ breachedRegulation: aiBreachText });
        setBreachDetailsDerivedFromAi(true);
    }, [aiBreachText, reviewEditingEnabled, markDeferredReviewEdited, form]);

    const markJustificationEdited = useCallback(() => {
        if (reviewEditingEnabled) setJustificationDerivedFromAi(false);
    }, [reviewEditingEnabled]);

    const markBreachDetailsEdited = useCallback(() => {
        if (reviewEditingEnabled) setBreachDetailsDerivedFromAi(false);
    }, [reviewEditingEnabled]);

    const handleTeachOnSubmitChange = useCallback(
        (checked: boolean) => {
            if (!reviewEditingEnabled) return;
            markDeferredReviewEdited();
            setTeachOnSubmit(checked);
        },
        [reviewEditingEnabled, markDeferredReviewEdited],
    );

    // Submit through the same handler, but hold position instead of advancing.
    const saveAndStay = useCallback(() => {
        if (!reviewEditingEnabled) return;
        stayAfterSubmitRef.current = true;
        form.submit();
    }, [reviewEditingEnabled, form]);

    // Reconciles the global breach flag with the per-obligation verdicts from the rail's warning.
    // `setFields` (not `setFieldsValue`) so the field is marked touched and the unsaved-changes
    // guard sees it — the reviewer did make this decision, they just made it in one click.
    const setBreachFlag = useCallback(
        (value: boolean) => {
            if (!reviewEditingEnabled) return;
            markDeferredReviewEdited();
            form.setFields([{ name: 'isBreachOfRegulation', value, touched: true }]);
        },
        [reviewEditingEnabled, markDeferredReviewEdited, form],
    );

    const actionableObligationCount = useMemo(
        () => (data ? countVisibleObligations(data.matchedObligations) : 0),
        [data],
    );
    const unresolvedAiBreach = useMemo(() => (data ? hasUnresolvedAiBreach(data.matchedObligations) : false), [data]);
    const classificationStatusNotice = isClassificationPending ? (
        <Alert
            type="info"
            showIcon
            title="Classification in progress"
            description="The review form will refresh when the new AI output is ready."
        />
    ) : isClassificationFailed && data ? (
        <Alert
            type="error"
            showIcon
            title="Classification failed"
            description={data.error || 'The classifier did not return a usable output.'}
        />
    ) : null;

    return (
        <PageController route={Route}>
            <OverlayPage
                title={
                    data && !hasBlockingError ? (
                        <ReviewWorkspaceTitle data={data} classification={aiOriginalClassification} />
                    ) : (
                        'Review classification'
                    )
                }
                open={open}
                isDirty={isReviewDirty}
                maskClosable={false}
                closable={!isTeachSettling}
                afterClose={() => {
                    navigate({
                        to: '/console/$orgId/tools/auto-detect/inbox',
                        params: { orgId },
                    });
                }}
                size="xxxLarge"
                classNames={{ body: 'bg-background !p-0' }}
                // The rail owns the commit actions; the render prop only captures `requestClose`.
                footer={({ requestClose }) => {
                    requestCloseRef.current = requestClose;
                    return null;
                }}
            >
                {isPending ? (
                    <div className="p-6">
                        <Skeleton active paragraph={{ rows: 8 }} />
                    </div>
                ) : !data || hasBlockingError ? (
                    <ClassificationUnavailable
                        error={error}
                        onRetry={() => void refetch()}
                        onBackToInbox={() => setOpen(false)}
                    />
                ) : (
                    <Form<FormValues>
                        form={form}
                        id={`review-classification-${id}`}
                        layout="vertical"
                        onFinish={handleSubmit}
                        onValuesChange={markDeferredReviewEdited}
                        disabled={!reviewEditingEnabled}
                        className="h-full min-h-0"
                    >
                        <ReviewWorkspace
                            statusNotice={classificationStatusNotice}
                            reviewDisabled={!reviewEditingEnabled}
                            recordLinksDisabled={isTeachSettling}
                            orgId={orgId}
                            data={data}
                            userRoles={user.roles}
                            currentIndex={currentIndex}
                            totalInQueue={visibleIds.length}
                            filterLabel={filterLabel}
                            reviewedThisSession={reviewedThisSession}
                            nextSubject={nextSubject}
                            onPrevious={prevId && !isTeachSettling ? () => navigateToId(prevId) : undefined}
                            onNext={nextId && !isTeachSettling ? () => navigateToId(nextId) : undefined}
                            obligationReviews={obligationReviews}
                            onChangeObligationReview={handleObligationReviewChange}
                            claimDecisions={claimDecisions}
                            onChangeClaimDecision={handleClaimDecisionChange}
                            currentIsIncident={watchedIsIncident ?? data.isIncident}
                            currentIsComplaint={watchedIsComplaint ?? data.isComplaint}
                            currentIsBreachOfRegulation={isBreachOfRegulation ?? data.isBreachOfRegulation}
                            currentConfidence={watchedConfidence ?? data.confidence}
                            showDebugInfo={enableDebugInfo}
                            breachedObligationCount={breachedObligationCount}
                            aiJustification={aiJustification}
                            railHeader={
                                <ReviewDecisionRailHeader
                                    changeCount={
                                        aiOriginalClassification
                                            ? differingFields.length + obligationOverrideCount
                                            : null
                                    }
                                    touched={reviewerActed}
                                />
                            }
                            railBody={
                                <ReviewDecisionRail
                                    aiOriginal={aiOriginalClassification}
                                    isBreachOfRegulation={isBreachOfRegulation}
                                    differingFields={differingFields}
                                    feedback={feedback}
                                    onFeedbackChange={handleFeedbackChange}
                                    breachedObligationCount={breachedObligationCount}
                                    actionableObligationCount={actionableObligationCount}
                                    hasUnresolvedAiBreach={unresolvedAiBreach}
                                    availableTags={availableTags ?? []}
                                    aiJustification={aiJustification}
                                    aiBreachText={aiBreachText}
                                    reviewerEditedBreachText={Boolean(data.reviewerEditedBreachText)}
                                    onUseAiJustification={useAiJustification}
                                    onUseAiBreachText={useAiBreachText}
                                    onJustificationEdited={markJustificationEdited}
                                    onBreachTextEdited={markBreachDetailsEdited}
                                    onSetBreach={setBreachFlag}
                                    onQuickNoIssue={applyNoIssueQuickVerdict}
                                    quickNoIssueDisabled={!reviewEditingEnabled}
                                    quickNoIssueActive={quickNoIssue}
                                    onCombinedFeedbackChange={handleCombinedFeedbackChange}
                                    teachOnSubmit={enableClassificationLearning && teachOnSubmit}
                                    onTeachOnSubmitChange={
                                        enableClassificationLearning ? handleTeachOnSubmitChange : undefined
                                    }
                                    teachDisabled={
                                        !reviewEditingEnabled || data.messageType === 'INTAKE' || draftedBreachOnly
                                    }
                                    teachDisabledReason={
                                        draftedBreachOnly
                                            ? 'Breach-only decisions cannot teach auto-detect — an affirmed breach is never learned, so this decision has nothing to teach.'
                                            : undefined
                                    }
                                    afterTeach={
                                        <SkipRuleAction
                                            messageClassificationId={data.id}
                                            visible={
                                                data.messageType === 'EMAIL' &&
                                                watchedIsIncident === false &&
                                                watchedIsComplaint === false &&
                                                !isBreachOfRegulation
                                            }
                                        />
                                    }
                                    teachCaption={
                                        <TeachOutcomeCaption
                                            watched={{
                                                isIncident: watchedIsIncident,
                                                isComplaint: watchedIsComplaint,
                                                isBreachOfRegulation,
                                            }}
                                            persisted={{
                                                isIncident: data.isIncident,
                                                isComplaint: data.isComplaint,
                                                isBreachOfRegulation: data.isBreachOfRegulation,
                                            }}
                                        />
                                    }
                                />
                            }
                            railFooter={
                                <ReviewDecisionRailFooter
                                    formId={`review-classification-${id}`}
                                    differingFields={reasonRequiredFields}
                                    feedback={feedback}
                                    onSkip={openSkipDialog}
                                    onSaveAndStay={saveAndStay}
                                    isSubmitting={isSubmitting || isTeachSettling}
                                    isSkipping={isSkipping}
                                    hasNextMessage={Boolean(nextId)}
                                    hasObligationOverrides={obligationOverrideCount > 0}
                                    reviewDisabled={!reviewEditingEnabled}
                                    skipDisabled={!canSkip || isTeachSettling}
                                    recordStep={
                                        enableRecordStep
                                            ? {
                                                  kinds: recordStepKinds({
                                                      isIncident: watchedIsIncident,
                                                      isComplaint: watchedIsComplaint,
                                                      hasIncident: (data.incidentIds?.length ?? 0) > 0,
                                                      hasComplaint: (data.complaintIds?.length ?? 0) > 0,
                                                      canCreate: recordStepAccess,
                                                  }),
                                                  enabled: recordStepEnabled,
                                                  onEnabledChange: handleRecordStepChange,
                                              }
                                            : undefined
                                    }
                                />
                            }
                        />
                    </Form>
                )}
            </OverlayPage>
            <SkipDialog
                open={skipOpen}
                onCancel={() => setSkipOpen(false)}
                onSubmit={handleSkipSubmit}
                submitting={isSkipping}
                disabled={!canSkip || isTeachSettling}
            />
            {enableClassificationLearning ? (
                <RecheckSimilarModal
                    target={recheckTarget}
                    onClose={() => {
                        setRecheckTarget(null);
                        // The submit branches hand their deferred navigation to the offer.
                        const after = afterRecheckRef.current;
                        afterRecheckRef.current = null;
                        if (after) performDeferredReviewAction(after);
                    }}
                />
            ) : null}
        </PageController>
    );
}

function buildLinkedClaimDecisionPayload(
    data: Data,
    localDecisions: ClaimDecisionState,
): ReviewInput['linkedClaimDecisions'] {
    // `claimSources` enumerates every claim the route's replace-all
    // semantics could touch: currently-visible matched + inferred claims
    // PLUS every server-persisted decision (filtered to readable on the
    // get-side). Omitting a readable persisted decision here would cause
    // `reviewMessageClassification` to silently delete it on the next
    // submit — see #1 in the M1+M2 review for the trigger scenario.
    const claimSources = new Map<string, MessageClassificationClaimLinkSource>();
    for (const claim of data.matchedClaims) {
        claimSources.set(claim.id, 'DETECTED');
    }
    for (const claim of data.inferredFromSenderClaims) {
        if (!claimSources.has(claim.id)) {
            claimSources.set(claim.id, 'INFERRED_FROM_SENDER');
        }
    }
    const serverDecisionsByClaimId = new Map(data.linkedClaimDecisions.map((decision) => [decision.claimId, decision]));
    for (const [claimId, decision] of serverDecisionsByClaimId) {
        if (!claimSources.has(claimId)) {
            claimSources.set(claimId, decision.source);
        }
    }

    return [...claimSources.entries()].flatMap(([claimId, defaultSource]) => {
        const localDecision = localDecisions[claimId];
        if (localDecision) {
            return localDecision.decision
                ? [{ claimId: BigInt(claimId), source: localDecision.source, decision: localDecision.decision }]
                : [];
        }

        const serverDecision = serverDecisionsByClaimId.get(claimId);
        if (serverDecision?.confirmed) {
            return [{ claimId: BigInt(claimId), source: serverDecision.source ?? defaultSource, decision: 'CONFIRM' }];
        }
        if (serverDecision?.rejected) {
            return [{ claimId: BigInt(claimId), source: serverDecision.source ?? defaultSource, decision: 'REJECT' }];
        }
        return [];
    });
}

function boolAsString(value: boolean | null | undefined): string | null {
    if (value === true) return 'true';
    if (value === false) return 'false';
    return null;
}

function aiValueForField(aiOriginalClassification: AiOriginalClassification, field: FeedbackField): string | null {
    switch (field) {
        case 'isIncident':
            return boolAsString(aiOriginalClassification.isIncident);
        case 'isComplaint':
            return boolAsString(aiOriginalClassification.isComplaint);
        case 'confidence':
            return aiOriginalClassification.confidence;
        case 'isBreachOfRegulation':
            return boolAsString(aiOriginalClassification.isBreachOfRegulation);
    }
}

function reviewerValueForField(
    form: ReturnType<typeof Form.useForm<FormValues>>[0],
    isBreachOfRegulation: boolean | undefined,
    field: FeedbackField,
): string | null {
    switch (field) {
        case 'isIncident':
            return boolAsString(form.getFieldValue('isIncident'));
        case 'isComplaint':
            return boolAsString(form.getFieldValue('isComplaint'));
        case 'confidence':
            return form.getFieldValue('confidence') ?? null;
        case 'isBreachOfRegulation':
            return boolAsString(isBreachOfRegulation);
    }
}
